import { Activity, Clock, FileCheck2, Files, Layers, TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { KpiStatTile } from '@/components/insights/kpi/KpiStatTile'
import type { ExtractionRun } from '@/services/api/cases'
import { cn } from '@/utils/cn'

export function PipelineStatsStrip({
  runs,
  compact,
}: {
  runs: ExtractionRun[]
  compact?: boolean
}) {
  const { t } = useTranslation()
  const total = runs.length
  const running = runs.filter(r => r.status === 'running').length
  const docsProcessed = runs.reduce((acc, r) => acc + r.completed_documents, 0)
  const docsFailed = runs.reduce((acc, r) => acc + r.failed_documents, 0)
  const docsTotal = runs.reduce((acc, r) => acc + r.total_documents, 0)
  const successRate =
    docsProcessed + docsFailed > 0
      ? `${Math.round((docsProcessed / (docsProcessed + docsFailed)) * 1000) / 10}%`
      : '—'

  const completedRuns = runs.filter(r => r.elapsed_ms != null && r.status !== 'running')
  const avgMs =
    completedRuns.length > 0
      ? completedRuns.reduce((acc, r) => acc + (r.elapsed_ms || 0), 0) / completedRuns.length
      : null
  const avgLabel = avgMs
    ? avgMs < 60000
      ? `${Math.round(avgMs / 1000)}s`
      : `${Math.round(avgMs / 60000)}m`
    : '—'

  const items = [
    { icon: Layers, label: t('extraction.totalRuns'), value: total.toString() },
    {
      icon: Activity,
      label: t('extraction.runningNow'),
      value: running.toString(),
      secondary: running > 0 ? 'in progress' : undefined,
    },
    {
      icon: FileCheck2,
      label: t('extraction.docsProcessed'),
      value: docsProcessed.toLocaleString(),
      secondary: docsFailed > 0 ? `${docsFailed} failed` : undefined,
    },
    { icon: Files, label: t('extraction.docsInScope'), value: docsTotal.toLocaleString() },
    {
      icon: TrendingUp,
      label: t('extraction.successRate'),
      value: successRate,
      secondary:
        docsProcessed + docsFailed > 0
          ? `${docsProcessed} / ${docsProcessed + docsFailed} docs`
          : undefined,
    },
    {
      icon: Clock,
      label: t('extraction.avgDuration'),
      value: avgLabel,
      secondary:
        completedRuns.length > 0
          ? `across ${completedRuns.length} run${completedRuns.length === 1 ? '' : 's'}`
          : undefined,
    },
  ]

  return (
    <div
      className={cn(
        // Phone: one swipeable row of tiles, so the run list stays above the fold.
        'scrollbar-none grid gap-3 max-md:-mx-4 max-md:flex max-md:overflow-x-auto max-md:px-4 max-md:[&>*]:min-w-36 max-md:[&>*]:shrink-0',
        compact
          ? 'grid-cols-2 sm:grid-cols-3'
          : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
      )}
    >
      {items.map(item => (
        <KpiStatTile
          key={item.label}
          icon={item.icon}
          label={item.label}
          value={item.value}
          secondary={item.secondary}
        />
      ))}
    </div>
  )
}
